import React, { useState } from 'react';
import { InspectionModalState } from './helper';

interface VariantProps {
  inspectionModal: InspectionModalState | null;
  onPhotoChange: (
    photoType: keyof Omit<InspectionModalState, 'reservationId'>,
    file: File | null
  ) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const InspectionModal: React.FC<VariantProps> = ({
  inspectionModal,
  onPhotoChange,
  onSubmit,
  onCancel
}) => {
  if (!inspectionModal) return null;

  const [currentStep, setCurrentStep] = useState(0);
  const steps = [
    { key: 'amenitiesPhoto', label: 'Amenities', hint: 'Kitchen, dining, living spaces' },
    { key: 'bedPhoto', label: 'Bed', hint: 'Bedroom cleanliness and condition' },
    { key: 'toiletPhoto', label: 'Toilet', hint: 'Bathroom cleanliness' },
    { key: 'livingAreaPhoto', label: 'Living Area', hint: 'Overall room condition' }
  ] as const;

  const allPhotos =
    inspectionModal.amenitiesPhoto &&
    inspectionModal.bedPhoto &&
    inspectionModal.toiletPhoto &&
    inspectionModal.livingAreaPhoto;
  const currentPhoto = inspectionModal[steps[currentStep].key];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl md:max-w-lg md:p-6 lg:max-w-xl lg:p-8">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-gilroy-semibold text-neutral-700 md:text-3xl lg:text-4xl">Inspection</h2>
            <span className="rounded-full bg-primary-100 px-3 py-1 text-sm font-gilroy-semibold text-primary-700 md:text-base">
              {currentStep + 1} / 4
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
            <div
              className="h-1.5 rounded-full bg-primary-500 transition-all"
              style={{ width: `${((currentStep + 1) / 4) * 100}%` }}
            />
          </div>
        </div>

        <div className="mb-6 md:mb-8">
          <h3 className="text-xl font-gilroy-semibold text-neutral-700 md:text-2xl lg:text-3xl">
            {steps[currentStep].label}
          </h3>
          <p className="mt-1 text-sm text-neutral-500 md:text-base lg:text-lg">{steps[currentStep].hint}</p>
        </div>

        <label className="flex w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-primary-300 bg-primary-50 p-4 transition-colors hover:bg-primary-100 md:p-6 lg:p-8">
          {currentPhoto ? (
            <div className="w-full">
              <img
                src={URL.createObjectURL(currentPhoto)}
                alt={steps[currentStep].label}
                className="h-64 w-full rounded-lg object-cover md:h-72 lg:h-80"
              />
              <span className="mt-3 block text-center text-base font-gilroy-semibold text-primary-700 md:text-lg lg:text-xl">
                {currentPhoto.name}
              </span>
              <span className="mt-1 block text-center text-sm text-neutral-500 md:text-base">
                Tap to replace photo
              </span>
            </div>
          ) : (
            <>
              <span className="mb-2 text-4xl md:text-5xl lg:text-6xl">📸</span>
              <span className="text-center text-base font-gilroy-semibold text-primary-700 md:text-lg lg:text-xl">
                Take or upload photo
              </span>
            </>
          )}
          <input
            key={steps[currentStep].key}
            className="hidden"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              onPhotoChange(steps[currentStep].key, e.target.files?.[0] || null);
              e.currentTarget.value = '';
            }}
          />
        </label>

        <div className="mt-6 flex items-center justify-between gap-3 md:mt-8 md:gap-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-4 py-2.5 text-base font-gilroy-semibold text-neutral-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 md:text-lg lg:text-xl md:px-6 md:py-3"
            disabled={currentStep === 0}
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          >
            Back
          </button>

          <button
            type="button"
            className="rounded-lg border border-gray-200 px-4 py-2.5 text-base font-gilroy-semibold text-neutral-600 transition-colors hover:bg-gray-50 md:text-lg lg:text-xl md:px-6 md:py-3"
            onClick={onCancel}
          >
            Cancel
          </button>

          {currentStep < 3 ? (
            <button
              type="button"
              className="flex-1 rounded-lg border border-primary-300 bg-primary-50 px-4 py-2.5 text-base font-gilroy-semibold text-primary-700 transition-colors hover:bg-primary-100 md:text-lg lg:text-xl md:px-6 md:py-3"
              onClick={() => setCurrentStep(currentStep + 1)}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="rounded-lg border border-primary-300 bg-primary-50 px-4 py-2.5 text-base font-gilroy-semibold text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50 md:text-lg lg:text-xl md:px-6 md:py-3"
              disabled={!allPhotos}
              onClick={onSubmit}
            >
              Submit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
